import { type CSSProperties, useCallback, useMemo, useRef, useState } from 'react'

import { getScrollParent, type ScrollParent } from '../useScrollParentRef'
import { useSizeEffect } from '../useSizeEffect'

type Props<TEntry, TBase> = {
	/** An array of entries to render. */
	data: Array<TEntry>
	/**
	 * A function to render an entry. This may be called both for rendering and
	 * for initial measuring.
	 */
	render: (entry: TEntry, index: number) => React.JSX.Element

	/**
	 * Size of each element. Support 4 different modes:
	 * * `undefined` - First element will be rendered and measured, all other
	 *   elements are assumed to be the same size.
	 * * A number - All elements are assumed to be this size in pixels.
	 * * A function mapping entries to sizes.
	 * * An object with a function to measure the first element element and
	 *   produce some arbitrary data, and a function to calculate other entries
	 *   size from this data.
	 */
	elementHeight?:
		| number
		| ((entry: TEntry, index: number) => number)
		| {
				measure: (element: Element, entry: TEntry) => TBase
				calculate: (base: TBase, entry: TEntry, index: number) => number
		  }

	/**
	 * Amount of pixels past the scroll boundry where entries should remain
	 * visible. A negative `overscan` will result in entries disappearing before
	 * leaving the screen (useful for debugging).
	 */
	overscan?: number
	/**
	 * Ensure a fixed amount of elements are always rendered while invisible, no
	 * matter the overscan. This is mostly useful for large elements with
	 * accessibility navigation.
	 */
	overscanElements?: number

	className?: string
	style?: CSSProperties
}

/**
 * List that only shows currently visible entries depending on the nearest
 * scroll-context. If it is not placed inside a scroll-context, the global
 * scroll context will be used.
 */
export const VirtualizedList = <TEntry, TBase>({
	data,
	render,
	elementHeight,
	overscan = 0,
	overscanElements = 0,
	style,
	className,
}: Props<TEntry, TBase>) => {
	// External height of the scroll container
	const [height, setHeight] = useState(0)
	// Offset from top of scroll container to start of list
	const [offsetTop, setOffsetTop] = useState(0)
	// Current scroll position
	const [scrollPosition, setScrollPosition] = useState(0)
	// Some data to assist in calculating element sizes. Should be TBase, but
	// this causes a lot of cast problems in the cases where TBase must be a
	// number due to `elementHeight` settings.
	const [elementHeightBase, setElementHeightBase] = useState<any>(() => {
		if (elementHeight === undefined || typeof elementHeight === 'object') {
			return null
		}
		if (typeof elementHeight === 'number') {
			return elementHeight
		}
		return 0
	})

	// Scrollable parent. Assumes a static overflow css property.
	const scrollParentRef = useRef<ScrollParent | undefined>(undefined)
	// A cleanup function to allow events to be unsubscribed.
	const cleanupRef = useRef<() => void>()
	// Add resize observer to element, to handle offset and size of scroll
	// container.
	const heightRef = useSizeEffect(() => {
		if (scrollParentRef.current) {
			setHeight(scrollParentRef.current.height)
			setOffsetTop(scrollParentRef.current.offsetTop)
		}
	})

	// Main ref for finding the scroll container
	const ref = useCallback(
		(element: HTMLElement | null) => {
			const scrollParent = element ? getScrollParent(element) : undefined
			scrollParentRef.current = scrollParent
			// Manually call the heightRef as a function here, we can't add it to an
			// element outside this component.
			heightRef(scrollParent?.scrollElement ?? null)

			if (cleanupRef.current) {
				cleanupRef.current()
				cleanupRef.current = undefined
			}

			if (!scrollParent) {
				return
			}

			setHeight(scrollParent.height)
			setOffsetTop(scrollParent.offsetTop)

			cleanupRef.current = scrollParent.subscribe('scroll', () => {
				setScrollPosition(scrollParent.scrollTop)
			})
		},
		[heightRef],
	)

	// Calculated the end-position of each element
	const positions = useMemo(() => {
		if (!elementHeight || typeof elementHeight === 'number' || elementHeightBase === null) return []
		let last = 0
		return data.map((entry, index) => {
			last +=
				typeof elementHeight === 'function'
					? elementHeight(entry, index)
					: elementHeight.calculate(elementHeightBase, entry, index)
			return last
		})
	}, [data, elementHeight, elementHeightBase])

	// Skip all the logic for empty lists
	if (data.length === 0) {
		return <div className={className} style={style} />
	}

	// Unknown element height, measure it
	if (elementHeightBase === null) {
		return (
			<div className={className} style={style}>
				<div
					ref={(element) => {
						if (!element) return

						if (typeof elementHeight === 'object') {
							setElementHeightBase(elementHeight.measure(element, data[0]))
						} else {
							setElementHeightBase(element.getBoundingClientRect().height)
						}
					}}
					style={{ overflow: 'hidden' }}
				>
					{render(data[0], 0)}
				</div>
			</div>
		)
	}

	const top = scrollPosition - overscan - offsetTop
	const bottom = scrollPosition + height + overscan - offsetTop
	let start: number
	let end: number
	let paddingTop: number
	let paddingBottom: number

	if (typeof elementHeight === 'function' || typeof elementHeight === 'object') {
		start = 0
		end = 1
		paddingTop = 0
		paddingBottom = positions[positions.length - 1] - positions[0]
		// Todo: use binary search to find start position quicker
		for (let index = 0; index < positions.length - 1 && positions[index] < bottom; index++) {
			if (positions[index] < top) {
				start = Math.max(0, index + 1 - overscanElements)
				paddingTop = positions[start - 1] ?? 0
			}
			end = Math.min(positions.length, index + 2 + overscanElements)
			paddingBottom = positions[positions.length - 1] - positions[end - 1]
		}
	} else {
		// In this case, elementHeightBase is either an externally supplied
		// number, or the size of the first element
		const fixedHeight = elementHeightBase as number
		start = Math.max(0, Math.min(Math.floor(top / fixedHeight) - overscanElements, data.length))
		end = Math.max(0, Math.min(Math.ceil(bottom / fixedHeight) + overscanElements, data.length))
		paddingTop = start * fixedHeight
		paddingBottom = (data.length - end) * fixedHeight
	}

	return (
		<div ref={ref} className={className} style={{ ...style, paddingTop, paddingBottom }}>
			{data.slice(start, end).map((entry, index) => render(entry, index + start))}
		</div>
	)
}
